import Actions from 'Actions';
import { createAction, createReducer, createAsyncThunk } from '@reduxjs/toolkit';
import { assert } from 'common/assertions';
import {
  WIZARD_STEP,
  assertColumns,
  componentPropertiesFromStoryUrl,
  getStepForComponent,
  getDatasetInVif,
} from 'lib/AssetSelectorHelper';

import { get, set, unset, extend, merge, cloneDeep, isEmpty, isObject } from 'lodash';
import { ComponentType, type BlockComponent } from 'types';
import { type Vif } from 'common/visualizations/vif';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import { I18n } from 'common/i18n';
import { extractIdFromYoutubeUrl } from 'lib/extractIdFromYoutubeUrl';
import Constants from 'lib/Constants';
import MetadataProvider from 'common/visualizations/dataProviders/MetadataProvider';
import { exceptionNotifier } from 'services/ExceptionNotifier';
import { type View } from 'common/types/view';
import { selectImageComponent } from 'store/selectors/AssetSelectorSelectors';
import { type Crop } from 'react-image-crop';

export interface AssetSelectorState {
  step: string | null;
  blockId: string | null;
  componentIndex: number | null;
  componentType: string | null;
  componentProperties: {
    // Common properties across component types
    dataset?: {
      datasetUid: string;
      vifId?: string;
    };
    measure?: {
      uid?: string;
      version?: string;
      metricConfig?: unknown;
      dataSourceLensUid?: string;
      custom_link?: { href: string; text: string };
      use_custom_link?: boolean;
      metadata?: { shortName?: string };
    };
    vif?: Vif;
    // Image component properties
    documentId?: string | null;
    url?: string;
    originalUrl?: string;
    alt?: string;
    link?: string;
    openInNewWindow?: boolean;
    urlValidity?: boolean;
    crop?: Crop;
    // Nested image for author component
    image?: {
      documentId?: string | null;
      url?: string;
      originalUrl?: string;
      alt?: string;
      urlValidity?: boolean;
      crop?: Crop;
    };
    // Other component properties
    id?: string; // YouTube ID
    title?: string; // YouTube/HTML title
    layout?: { height?: number };
    visualization?: unknown;
    originalUid?: string;
    isInsitu?: boolean;
    // Allow additional properties for flexibility
    [key: string]: unknown;
  };
  dataset: View | null;
  isEditingExisting: boolean;
  isBusy: boolean;
  originalComponentProperties: {
    [key: string]: unknown;
  };
  originalComponentType: string | null;
  previewImageUrl?: string;
  datasetUserSelectedFromList?: string;
}

const initialState: AssetSelectorState = {
  step: null,
  blockId: null,
  componentIndex: null,
  componentType: null,
  componentProperties: {},
  dataset: null,
  isEditingExisting: false,
  isBusy: false,
  originalComponentProperties: {},
  originalComponentType: null,
  previewImageUrl: undefined,
  datasetUserSelectedFromList: undefined,
};

// Type for component values passed to actions
export type ComponentValue = AssetSelectorState['componentProperties'];

// Async thunk for fetching dataset metadata when editing existing components
export const editExistingAssetEmbedWithData = createAsyncThunk(
  'assetSelector/editExistingAssetEmbedWithData',
  async (
    payload: {
      blockId: string;
      componentIndex: number;
      componentType: string;
      componentValue: ComponentValue;
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      dispatch(editExistingAssetEmbed(payload));
      const { componentType, componentValue } = payload;

      // Skip dataset fetch for insitu measures (they use saved data instead)
      if (componentType.includes('measure') && componentValue.isInsitu) {
        return null;
      }

      const datasetId =
        componentValue?.dataset?.datasetUid ||
        getDatasetInVif({
          componentProperties: componentValue,
        } as AssetSelectorState);
      if (
        !datasetId ||
        componentType === 'socrata.visualization.vizCanvas' ||
        componentType === ComponentType.GLOBAL_FILTER
      ) {
        return null;
      }

      const metadataProvider = new MetadataProvider({ datasetUid: datasetId });
      const viewData = await metadataProvider.getDatasetMetadata();

      // Handle dataset redirects (legacy NBE migration logic)
      if (viewData.newBackend && viewData.id !== datasetId) {
        viewData.id = datasetId;
      }

      return { viewData };
    } catch (error) {
      exceptionNotifier.notify(error);
      return rejectWithValue({ error: error.message });
    }
  },
);

// Core asset selector actions

export const selectAssetForComponent = createAction<{
  blockId: string;
  componentIndex: number;
  componentType?: string;
  step?: string;
  existingContent?: Partial<AssetSelectorState['componentProperties']>;
}>(Actions.ASSET_SELECTOR_SELECT_ASSET_FOR_COMPONENT);

export const editExistingAssetEmbed = createAsyncThunk(
  'assetSelector/editExistingAssetEmbed',
  async (payload: {
    blockId: string;
    componentIndex: number;
    componentType: string;
    componentValue: ComponentValue;
  }) => {
    const { componentType, componentValue } = payload;

    let previewImageUrl: string | null = null;

    if (['image', 'hero', 'author'].includes(componentType)) {
      const imageComponent = selectImageComponent({
        assetSelector: {
          componentType: componentType,
          componentProperties: componentValue,
        } as AssetSelectorState,
      });
      if (imageComponent.documentId) {
        try {
          const { getSignedUrls } = await import('lib/AssetSelectorHelper');
          const document = await getSignedUrls(imageComponent.documentId);
          previewImageUrl = document.originalUrl ?? null;
        } catch (error) {
          console.error('Failed to fetch signed URLs for image:', error);
        }
      } else {
        // If documentId is empty, then this is a Getty Image or the user's profile image.
        previewImageUrl = imageComponent.url ?? null;
      }
    }

    if (componentType === 'embeddedHtml' && componentValue.documentId) {
      try {
        const { getSignedUrls } = await import('lib/AssetSelectorHelper');
        await getSignedUrls(componentValue.documentId);
      } catch (error) {
        console.error('Failed to fetch signed URLs for HTML:', error);
      }
    }

    return { ...payload, previewImageUrl };
  },
);

export const updateComponentType = createAction<string>(Actions.ASSET_SELECTOR_UPDATE_COMPONENT_TYPE);

export const jumpToStep = createAction<string>(Actions.ASSET_SELECTOR_JUMP_TO_STEP);

export const closeAssetSelector = createAction(Actions.ASSET_SELECTOR_CLOSE);

export const setBusy = createAction<boolean>(Actions.ASSET_SELECTOR_SET_BUSY);

// Image-specific actions

export const updateImageData = createAction<{
  imageData: {
    documentId?: string;
    url?: string;
    originalUrl?: string;
  };
}>(Actions.ASSET_SELECTOR_UPDATE_IMAGE_DATA);

export const chooseImageUpload = createAction(Actions.ASSET_SELECTOR_CHOOSE_IMAGE_UPLOAD);

// Dataset and visualization actions

export const chooseVisualizationDataset = createAction<{
  viewData: NonNullable<AssetSelectorState['dataset']>;
}>(Actions.ASSET_SELECTOR_CHOOSE_VISUALIZATION_DATASET);

export const chooseMeasure = createAction<string>(Actions.ASSET_SELECTOR_CHOOSE_MEASURE);

export const chooseVisualization = createAction(Actions.ASSET_SELECTOR_CHOOSE_VISUALIZATION);

export const chooseVisualizationMapOrChartOrCalendar = createAction<{
  mapOrChartUid: string;
  viewData: NonNullable<AssetSelectorState['dataset']>;
}>(Actions.ASSET_SELECTOR_CHOOSE_VISUALIZATION_MAP_OR_CHART_OR_CALENDAR);

export const updateVisualizationConfiguration = createAction<{
  visualization: {
    data: unknown;
    format: 'vizCanvas' | 'vif' | 'vif2';
    originalUid?: string;
  };
}>(Actions.ASSET_SELECTOR_UPDATE_VISUALIZATION_CONFIGURATION);

export const updateMeasureConfiguration = createAction<{
  measure: NonNullable<AssetSelectorState['componentProperties']['measure']>;
}>(Actions.ASSET_SELECTOR_UPDATE_MEASURE_CONFIGURATION);

// Provider and option selection actions

export const providerChosen = createAction<{
  blockId: string;
  componentIndex: number;
  provider: string;
}>(Actions.ASSET_SELECTOR_PROVIDER_CHOSEN);

export const visualizationOptionChosen = createAction<string>(
  Actions.ASSET_SELECTOR_VISUALIZATION_OPTION_CHOSEN,
);

export const measureOptionChosen = createAction<string>(Actions.ASSET_SELECTOR_MEASURE_OPTION_CHOSEN);

export const otherMediaOptionChosen = createAction<string>(Actions.ASSET_SELECTOR_OTHER_MEDIA_OPTION_CHOSEN);

// URL and content actions

export const updateYoutubeUrl = createAction<string>(Actions.ASSET_SELECTOR_UPDATE_YOUTUBE_URL);
export const updateStoryUrl = createAction<string>(Actions.ASSET_SELECTOR_UPDATE_STORY_URL);
export const updateEmbedCode = createAction<{
  embedData: {
    url: string;
    documentId: string;
  };
}>(Actions.ASSET_SELECTOR_UPDATE_EMBED_CODE);
export const chooseEmbedCode = createAction<string>(Actions.ASSET_SELECTOR_CHOOSE_EMBED_CODE);

// Custom measure actions

export const updateCustomMeasureHref = createAction<string>(
  Actions.ASSET_SELECTOR_UPDATE_CUSTOM_MEASURE_HREF,
);
export const updateCustomMeasureText = createAction<string>(
  Actions.ASSET_SELECTOR_UPDATE_CUSTOM_MEASURE_TEXT,
);
export const setIsCustomMeasureLinkEnabled = createAction<boolean>(
  Actions.ASSET_SELECTOR_IS_CUSTOM_MEASURE_LINK_ENABLED,
);

// Other actions

export const updateTitleAttribute = createAction<string>(Actions.ASSET_SELECTOR_UPDATE_TITLE_ATTRIBUTE);
export const toggleStoryWindowTarget = createAction(Actions.ASSET_SELECTOR_TOGGLE_STORY_WINDOW_TARGET);
export const cancelFileUpload = createAction(Actions.ASSET_SELECTOR_CANCEL_FILE_UPLOAD);

// Component-level async helper actions

const updateComponentProperties = (state: AssetSelectorState, updates: Partial<ComponentValue>): void => {
  state.componentProperties = { ...state.componentProperties, ...updates };
};

export const chooseMeasureOptionStep = (): string => {
  const enableInStoryMeasures = FeatureFlags.valueOrDefault('enable_in_story_measures');
  const enableOPstandaloneMeasures = FeatureFlags.valueOrDefault('open_performance_standalone_measures');

  return enableInStoryMeasures && !enableOPstandaloneMeasures
    ? WIZARD_STEP.AUTHOR_MEASURE
    : WIZARD_STEP.SELECT_MEASURE_OPTION;
};

// Helper function for insitu measure editing (equivalent to _setComponentPropertiesFromMeasureData)
const setComponentPropertiesFromMeasureData = (
  state: AssetSelectorState,
  component: { value: ComponentValue },
) => {
  if (!component.value?.measure) {
    return;
  }

  const measureData = component.value.measure;
  const isCustomLinkSet = measureData.use_custom_link === true;

  if (state.componentProperties.measure?.metricConfig) {
    extend(state.componentProperties, {
      measure: {
        version: measureData.version,
        metricConfig: measureData.metricConfig,
        dataSourceLensUid: measureData.dataSourceLensUid,
        ...(isCustomLinkSet && { custom_link: measureData.custom_link }),
        ...(isCustomLinkSet && { use_custom_link: measureData.use_custom_link }),
        metadata: {
          shortName: measureData.metadata?.shortName,
        },
      },
    });
  }
};

export const assetSelectorReducer = createReducer(initialState, (builder) => {
  builder

    // Core asset selector actions
    .addCase(selectAssetForComponent, (state, action) => {
      const { blockId, componentIndex, componentType, step, existingContent } = action.payload;
      state.blockId = blockId;
      state.componentIndex = componentIndex;
      state.componentType = componentType || null;
      state.step = step || WIZARD_STEP.SELECT_ASSET_PROVIDER;
      state.isEditingExisting = false;
      state.originalComponentType = null;
      state.originalComponentProperties = {};
      // Preserve existing content if provided, otherwise start with empty object
      state.componentProperties = existingContent ? { ...existingContent } : {};
      state.dataset = null;
      state.previewImageUrl = undefined;
    })

    .addCase(editExistingAssetEmbed.pending, () => {
      // Don't set up any state here - wait for fulfilled to do everything atomically like old Flux
    })

    .addCase(editExistingAssetEmbed.fulfilled, (state, action) => {
      // Set up ALL state atomically in one update, just like old Flux store
      const { blockId, componentIndex, componentType, componentValue, previewImageUrl } = action.payload;

      state.blockId = blockId;
      state.componentIndex = componentIndex;
      state.componentType = componentType;
      state.componentProperties = cloneDeep(componentValue);
      state.isEditingExisting = true;
      state.originalComponentType = componentType;
      state.originalComponentProperties = cloneDeep(componentValue);
      state.step = getStepForComponent({ type: componentType } as BlockComponent);

      if (previewImageUrl) {
        state.previewImageUrl = previewImageUrl;
      }

      if (componentType.includes('measure') && state.componentProperties.isInsitu) {
        setComponentPropertiesFromMeasureData(state, { value: componentValue });
        return;
      }

      if (['image', 'hero', 'author'].includes(componentType)) {
        const value = selectImageComponent({
          assetSelector: state,
        });
        // Only set if we didn't already get a signed URL
        if (!value.documentId && !previewImageUrl) {
          state.previewImageUrl = value.url ?? null;
        }
        if (componentType === 'image') {
          state.componentProperties.urlValidity = true;
        } else if (componentType === 'hero') {
          state.componentProperties.urlValidity = true;
        } else if (componentType === 'author' && state.componentProperties.image) {
          state.componentProperties.image.urlValidity = true;
        }
      }
    })

    .addCase(editExistingAssetEmbed.rejected, (_, action) => {
      console.error('Failed to setup asset editing:', action.error);
    })

    .addCase(updateComponentType, (state, action) => {
      state.componentType = action.payload;
    })

    .addCase(jumpToStep, (state, action) => {
      state.step = action.payload;
    })

    .addCase(closeAssetSelector, () => {
      return initialState;
    })

    .addCase(setBusy, (state, action) => {
      state.isBusy = action.payload;
    })

    // Image actions

    .addCase(updateImageData, (state, action) => {
      const { imageData } = action.payload;
      const properties = state.componentProperties || {};

      switch (state.componentType) {
        case 'author': {
          // Author component expects image data to be nested inside its data
          const existingImage = properties.image || {};
          state.componentProperties = {
            ...properties,
            image: {
              ...existingImage,
              documentId: imageData.documentId,
              url: imageData.url,
              originalUrl: imageData.originalUrl,
              crop: undefined,
            },
          };
          break;
        }

        case 'hero':
          // Hero component expects image data to live alongside its data
          state.componentProperties = {
            ...properties,
            documentId: imageData.documentId,
            url: imageData.url,
            originalUrl: imageData.originalUrl,
            crop: undefined,
          };
          break;

        case 'image':
          // Image component expects image data to be its data
          state.componentProperties = {
            ...properties,
            documentId: imageData.documentId,
            url: imageData.url,
            originalUrl: imageData.originalUrl,
            crop: undefined,
          };
          break;

        default:
          throw new Error(`Invalid component type ${state.componentType} for image processing!`);
      }
    })

    .addCase(chooseImageUpload, (state) => {
      if (state.componentType === 'image') {
        state.componentProperties = merge(state.componentProperties || {}, {
          urlValidity: true,
          openInNewWindow: false,
        });
      }
      state.step = WIZARD_STEP.SELECT_IMAGE_TO_UPLOAD;
    })

    // Dataset and visualization actions

    .addCase(chooseVisualizationDataset, (state, action) => {
      const { viewData } = action.payload;
      const isCreatingTable = state.componentType === 'socrata.visualization.agTable';
      state.componentProperties = state.componentProperties || {};
      extend(state.componentProperties, {
        dataset: {
          datasetUid: viewData.id,
        },
      });

      state.dataset = cloneDeep(viewData);
      state.datasetUserSelectedFromList = viewData.id;
      if (isCreatingTable) {
        // If the user has selected "TABLE", then pre-populate the vif with the viz type
        set(state, 'componentProperties.vif.series[0].type', 'agTable');
        state.step = WIZARD_STEP.AUTHOR_VISUALIZATION;
      } else if (state.componentType === ComponentType.GLOBAL_FILTER) {
        assertColumns(state);
        state.step = WIZARD_STEP.SELECT_DATASET_FOR_STORY_GLOBAL_FILTER;
      } else {
        // If we previously configured a table in this AssetSelector session, the vif.type is set to "table"
        // which screws with the AX's initial state and causes a bug with the chart type picker

        if (get(state, 'componentProperties.vif.type') === 'table') {
          unset(state, 'componentProperties.vif.type');
        }
        state.step = WIZARD_STEP.AUTHOR_VISUALIZATION;
      }
    })

    .addCase(chooseMeasure, (state, action) => {
      const uid = action.payload;
      state.componentType = 'measure.card';
      state.componentProperties = state.componentProperties || {};

      extend(state.componentProperties, {
        measure: { uid: uid },
      });

      state.step = WIZARD_STEP.CONFIGURE_MEASURE;
    })

    .addCase(chooseVisualizationMapOrChartOrCalendar, (state, action) => {
      const { viewData } = action.payload;
      const isVizCanvasView =
        isObject(viewData) &&
        (viewData.displayType === 'visualization' || // legacy display type, still VizCanvas
          viewData.displayType === 'visualization_canvas_chart' ||
          viewData.displayType === 'visualization_canvas_calendar' ||
          viewData.displayType === 'visualization_canvas_map');
      if (isVizCanvasView) {
        state.step = WIZARD_STEP.CONFIGURE_MAP_OR_CHART_OR_CALENDAR;
        state.componentProperties = state.componentProperties || {};
        extend(state.componentProperties, {
          dataset: {
            datasetUid: viewData.id,
          },
        });
        state.dataset = cloneDeep(viewData);
        // For now, while we only have one visualization per canvas, we're going to default to the first one
        const vifId = viewData.displayFormat?.visualizationCanvasMetadata?.vifs[0]?.id;
        if (vifId) {
          extend(state.componentProperties.dataset, { vifId });
        }
      } else {
        // don't advance the step — keep the user in the picker so they can choose a valid asset
        console.error(I18n.t('editor.asset_selector.visualization.choose_map_or_chart_error'));
      }
    })

    .addCase(updateVisualizationConfiguration, (state, action) => {
      const { visualization: visualizationPayload } = action.payload;
      const visualization = visualizationPayload.data as any; // Legacy visualization data has dynamic structure
      const format = visualizationPayload.format;
      if (isEmpty(visualization)) {
        state.componentType = null;
        state.componentProperties = {
          dataset: state.componentProperties.dataset,
        };
      } else if (format === 'vizCanvas') {
        state.componentType = 'socrata.visualization.vizCanvas';
        state.componentProperties = {
          dataset: state.componentProperties.dataset,
        };
      } else if (format === 'vif') {
        state.componentType = `socrata.visualization.${visualization.type}`;
        state.componentProperties = {
          vif: visualization,
          dataset: state.componentProperties.dataset,
          originalUid: visualizationPayload.originalUid,
        };
      } else if (format === 'vif2') {
        // If we are updating a visualization, we need to check if its layout
        // height has been previously set and remember it so that it can be added
        // back into the new component properties after they have been reset.

        const layoutHeight = get(state, 'componentProperties.layout.height');
        const visualizationType = visualization.series[0].type.split('.')[0];
        state.componentType = `socrata.visualization.${visualizationType}`;

        state.componentProperties = {
          vif: visualization,
          dataset: state.componentProperties.dataset,
          originalUid: visualizationPayload.originalUid,
        };
        // If layout height was previously set, set the same layout height in the
        // new component properties.
        if (layoutHeight) {
          set(state, 'componentProperties.layout.height', layoutHeight);
        }
      }
    })

    .addCase(updateMeasureConfiguration, (state, action) => {
      const { measure } = action.payload;

      if (isEmpty(measure)) {
        state.componentType = null;
      } else {
        if (!state.componentType) {
          state.componentType = 'measure.card';
        }
        state.componentProperties = {
          measure: measure,
        };
        set(state.componentProperties, 'isInsitu', true);
      }
    })

    // Provider and option selection

    .addCase(providerChosen, (state, action) => {
      const { provider } = action.payload;
      switch (provider) {
        case 'TABLE':
          state.step = WIZARD_STEP.SELECT_DATASET_FOR_VISUALIZATION;
          // If the user chose table, we are going to pre-populate the vif with the agTable viz type
          state.componentType = 'socrata.visualization.agTable';
          break;

        case 'SOCRATA_VISUALIZATION':
          // Check feature flag for visualization option
          if (FeatureFlags.valueOrDefault('enable_standalone_visualization_creation', true)) {
            state.step = WIZARD_STEP.SELECT_VISUALIZATION_OPTION;
          } else {
            state.step = WIZARD_STEP.SELECT_DATASET_FOR_VISUALIZATION;
          }
          break;

        case 'MEASURE': {
          const enableInStoryMeasures = FeatureFlags.valueOrDefault('enable_in_story_measures');
          const enableOPstandaloneMeasures = FeatureFlags.valueOrDefault(
            'open_performance_standalone_measures',
          );
          state.step =
            enableInStoryMeasures && !enableOPstandaloneMeasures
              ? WIZARD_STEP.AUTHOR_MEASURE
              : WIZARD_STEP.SELECT_MEASURE_OPTION;
          break;
        }

        case 'OTHER_MEDIA':
          state.step = WIZARD_STEP.SELECT_OTHER_OPTION;
          break;

        case 'GLOBAL_FILTER':
          state.componentType = ComponentType.GLOBAL_FILTER;
          state.step = WIZARD_STEP.SELECT_DATASET_FOR_STORY_GLOBAL_FILTER;
          break;

        case 'HERO':
          state.componentType = 'hero';
          state.step = WIZARD_STEP.SELECT_IMAGE_TO_UPLOAD;
          break;

        case 'IMAGE':
          state.componentType = 'image';
          state.componentProperties = merge(state.componentProperties || {}, {
            urlValidity: true,
            openInNewWindow: false,
          });
          state.step = WIZARD_STEP.SELECT_IMAGE_TO_UPLOAD;
          break;

        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }
    })

    .addCase(visualizationOptionChosen, (state, action) => {
      const visualizationOption = action.payload;
      state.componentType = null;

      switch (visualizationOption) {
        case 'INSERT_VISUALIZATION':
          state.step = WIZARD_STEP.SELECT_MAP_OR_CHART_OR_CALENDAR_VISUALIZATION_FROM_CATALOG;
          break;
        case 'INSERT_MEASURE':
          state.step = WIZARD_STEP.SELECT_MEASURE_FROM_CATALOG;
          break;
        case 'INSERT_TABLE':
          state.componentType = 'socrata.visualization.table';
          state.step = WIZARD_STEP.SELECT_TABLE_FROM_CATALOG;
          break;
        case 'AUTHOR_VISUALIZATION':
          state.step = WIZARD_STEP.SELECT_DATASET_FOR_VISUALIZATION;
          break;
      }
    })

    .addCase(measureOptionChosen, (state, action) => {
      const measureOption = action.payload;
      state.componentType = null;
      state.componentProperties = {};

      switch (measureOption) {
        case 'INSERT_MEASURE':
          state.step = WIZARD_STEP.SELECT_MEASURE_FROM_CATALOG;
          break;
        case 'AUTHOR_MEASURE':
          state.step = WIZARD_STEP.AUTHOR_MEASURE;
          break;
      }
    })

    .addCase(otherMediaOptionChosen, (state, action) => {
      const otherOption = action.payload;
      state.componentType = null;

      switch (otherOption) {
        case 'YOUTUBE':
          state.componentType = 'youtube.video';
          state.step = WIZARD_STEP.ENTER_YOUTUBE_URL;
          break;

        case 'STORY_TILE':
          state.step = WIZARD_STEP.ENTER_STORY_URL;
          break;

        case 'EMBED_CODE':
          state.componentType = 'embeddedHtml';
          state.step = WIZARD_STEP.ENTER_EMBED_CODE;
          break;
      }
    })

    // URL and content actions

    .addCase(updateYoutubeUrl, (state, action) => {
      const url = action.payload;
      const youtubeId = extractIdFromYoutubeUrl(url);
      state.componentType = 'youtube.video';
      const existingTitle = state.componentProperties?.title;
      state.componentProperties = {
        id: youtubeId,
        url: url,
        ...(existingTitle && { title: existingTitle }),
      };
    })

    .addCase(updateStoryUrl, (state, action) => {
      const url = action.payload;
      state.componentType = 'story.tile';
      const openInNewWindow = get(state.componentProperties, 'openInNewWindow', false);
      state.componentProperties = merge(componentPropertiesFromStoryUrl(url), { openInNewWindow });
    })

    .addCase(updateEmbedCode, (state, action) => {
      const { embedData } = action.payload;
      const { url, documentId } = embedData;

      state.componentType = 'embeddedHtml';
      const existingTitle = state.componentProperties?.title;
      state.componentProperties = {
        url: url,
        documentId: documentId,
        layout: {
          height: Constants.DEFAULT_VISUALIZATION_HEIGHT,
        },
        ...(existingTitle && { title: existingTitle }),
      };
    })

    .addCase(chooseEmbedCode, (state) => {
      state.step = WIZARD_STEP.ENTER_EMBED_CODE;
      state.componentProperties = {};
    })

    // Custom measure actions

    .addCase(updateCustomMeasureHref, (state, action) => {
      const href = action.payload;
      // Update componentProperties to store in measure.custom_link.href
      const measure = state.componentProperties?.measure || {};
      const customLink = measure.custom_link || { href: '', text: '' };
      updateComponentProperties(state, {
        measure: {
          ...measure,
          custom_link: { ...customLink, href },
        },
      });
    })

    .addCase(updateCustomMeasureText, (state, action) => {
      const text = action.payload;
      const measure = state.componentProperties?.measure || {};
      const customLink = measure.custom_link || { href: '', text: '' };
      updateComponentProperties(state, {
        measure: {
          ...measure,
          custom_link: { ...customLink, text },
        },
      });
    })

    .addCase(setIsCustomMeasureLinkEnabled, (state, action) => {
      const enabled = action.payload;
      const measure = state.componentProperties?.measure || {};
      updateComponentProperties(state, {
        measure: {
          ...measure,
          use_custom_link: enabled,
        },
      });
    })

    // Other actions
    .addCase(updateTitleAttribute, (state, action) => {
      const titleAttribute = action.payload;
      const value = state.componentProperties;
      if (state.componentType === 'youtube.video' || state.componentType === 'embeddedHtml') {
        value.title = titleAttribute;
      } else {
        throw new Error(`Component type is ${state.componentType}. Cannot update title attribute.`);
      }
    })

    .addCase(toggleStoryWindowTarget, (state) => {
      assert(state.componentType === 'story.tile', 'Component type must be story.tile');
      const value = state.componentProperties;
      value.openInNewWindow = !value.openInNewWindow;
    })

    .addCase(cancelFileUpload, (state) => {
      state.isBusy = false;
      state.previewImageUrl = undefined;
    })

    // Async thunk cases for dataset fetching
    .addCase(editExistingAssetEmbedWithData.pending, (state) => {
      state.isBusy = true;
    })

    .addCase(editExistingAssetEmbedWithData.fulfilled, (state, action) => {
      state.isBusy = false;

      if (action.payload) {
        const { viewData } = action.payload;

        // Set dataset metadata in state (equivalent to _setComponentPropertiesFromViewData)
        state.componentProperties = state.componentProperties || {};
        extend(state.componentProperties, {
          dataset: {
            datasetUid: viewData.id,
          },
        });
        state.dataset = cloneDeep(viewData);
      }
    })

    .addCase(editExistingAssetEmbedWithData.rejected, (state, action) => {
      state.isBusy = false;
      const currentStep = state.step;
      if (currentStep === WIZARD_STEP.CONFIGURE_MAP_OR_CHART_OR_CALENDAR) {
        state.step = WIZARD_STEP.SELECT_MAP_OR_CHART_OR_CALENDAR_VISUALIZATION_FROM_CATALOG;
      } else {
        state.step = WIZARD_STEP.SELECT_ASSET_PROVIDER;
      }
      console.error('Dataset fetch failed:', action.payload);
    });
});
